import { hasValidAntiSpam, withAntiSpam } from './antiSpam.js';
import { isValidPlayerId, playerIdFromPeerId } from '../security/input.js';
import { lobbyDebug } from './lobbyDebug.js';

export const CHAT_MESSAGE_TYPE = 'dechess-chat';
export const CHAT_ROOM_MESSAGE_TYPE = 'dechess-chat-room';
export const MAIN_CHAT_ROOM_ID = 'main';
export const MAIN_CHAT_ROOM_TITLE = 'Main Room';
export const CHAT_MESSAGE_TTL_MS = 2 * 60 * 60 * 1000;
export const CHAT_HISTORY_LIMIT = 100;
export const CHAT_ROOM_TITLE_MAX = 32;
export const CHAT_TEXT_MAX = 280;

const ROOM_ID_RE = /^[a-z0-9][a-z0-9-]{0,31}$/;

export function chatScope(roomId = MAIN_CHAT_ROOM_ID) {
  const id = normalizeRoomId(roomId) || MAIN_CHAT_ROOM_ID;
  return `lobby:chat:${id}`;
}

export function normalizeRoomId(value) {
  const raw = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);
  if (!raw || !ROOM_ID_RE.test(raw)) {
    return '';
  }
  return raw;
}

export function roomIdFromTitle(title) {
  return normalizeRoomId(String(title || '').trim()) || '';
}

export function isMainChatRoom(roomId) {
  return normalizeRoomId(roomId) === MAIN_CHAT_ROOM_ID;
}

export function defaultChatRooms() {
  return [{
    roomId: MAIN_CHAT_ROOM_ID,
    title: MAIN_CHAT_ROOM_TITLE,
    createdBy: '',
    createdAt: 0,
    builtin: true
  }];
}

export function buildChatMessagePayload({
  roomId = MAIN_CHAT_ROOM_ID,
  text = '',
  nickname = '',
  playerId = '',
  sentAt = Date.now()
}) {
  const id = normalizeRoomId(roomId) || MAIN_CHAT_ROOM_ID;
  const body = String(text || '').trim().slice(0, CHAT_TEXT_MAX);
  if (!body || !isValidPlayerId(playerId)) {
    return null;
  }
  return {
    roomId: id,
    text: body,
    nickname: String(nickname || '').trim().slice(0, 32),
    playerId,
    sentAt
  };
}

export function buildChatRoomPayload({
  roomId,
  title = '',
  createdBy = '',
  createdAt = Date.now()
}) {
  const id = normalizeRoomId(roomId);
  if (!id || isMainChatRoom(id) || !isValidPlayerId(createdBy)) {
    return null;
  }
  const label = String(title || id).trim().slice(0, CHAT_ROOM_TITLE_MAX) || id;
  return {
    roomId: id,
    title: label,
    createdBy,
    createdAt
  };
}

export async function sealChatMessage(payload) {
  const base = buildChatMessagePayload(payload);
  if (!base) {
    return null;
  }
  return withAntiSpam(base, 'chat');
}

export async function sealChatRoom(payload) {
  const base = buildChatRoomPayload(payload);
  if (!base) {
    return null;
  }
  return withAntiSpam(base, 'room');
}

export async function parseIncomingChatMessage(message, {
  now = Date.now(),
  requireAntiSpam = true
} = {}) {
  if (message?.type !== CHAT_MESSAGE_TYPE) {
    return null;
  }
  const payload = message.payload;
  if (!payload || !buildChatMessagePayload(payload)) {
    return null;
  }
  if (requireAntiSpam && !(await hasValidAntiSpam(payload, 'chat'))) {
    return null;
  }
  if (now - Number(payload.sentAt || 0) > CHAT_MESSAGE_TTL_MS) {
    return null;
  }
  const senderPlayerId = playerIdFromPeerId(message.senderId);
  if (
    senderPlayerId
    && String(payload.playerId).toLowerCase() !== String(senderPlayerId).toLowerCase()
  ) {
    return null;
  }
  return {
    ...payload,
    senderId: message.senderId,
    receivedAt: now,
    messageKey: `${message.senderId}:${payload.sentAt}:${payload.text.slice(0, 24)}`
  };
}

export async function parseIncomingChatRoom(message, {
  now = Date.now(),
  requireAntiSpam = true
} = {}) {
  if (message?.type !== CHAT_ROOM_MESSAGE_TYPE) {
    return null;
  }
  const payload = message.payload;
  if (!payload || !buildChatRoomPayload(payload)) {
    return null;
  }
  if (requireAntiSpam && !(await hasValidAntiSpam(payload, 'room'))) {
    return null;
  }
  const senderPlayerId = playerIdFromPeerId(message.senderId);
  if (
    senderPlayerId
    && String(payload.createdBy).toLowerCase() !== String(senderPlayerId).toLowerCase()
  ) {
    return null;
  }
  return {
    ...payload,
    senderId: message.senderId,
    receivedAt: now,
    builtin: false
  };
}

export function mergeChatMessages(existing = [], incoming, {
  limit = CHAT_HISTORY_LIMIT,
  now = Date.now()
} = {}) {
  if (!incoming) {
    return existing.filter((entry) => now - Number(entry.sentAt || 0) <= CHAT_MESSAGE_TTL_MS)
      .slice(-limit);
  }
  const next = existing.filter((entry) => entry.messageKey !== incoming.messageKey);
  next.push(incoming);
  return next
    .filter((entry) => now - Number(entry.sentAt || 0) <= CHAT_MESSAGE_TTL_MS)
    .sort((left, right) => Number(left.sentAt || 0) - Number(right.sentAt || 0))
    .slice(-limit);
}

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function openPeerIds(node) {
  return node?.getConnectionStats?.().peerIds || [];
}

/**
 * Deliver chat the way dignity.js chess demos keep PeerJS alive:
 * joinDiscovery({ bootstrapPeerIds }) + connectToPeers, then broadcast only to
 * open channels. PeerBasket publish covers Online-now peers with no link.
 *
 * "delivered" means published to PeerBasket OR sent on at least one open channel.
 * Queued-only adverts are pending, not delivered.
 */
export async function deliverChatPayload(node, type, payload, {
  connectToPeers = [],
  broadcastScope,
  attempts = 3,
  settleMs = 150,
  publishOutOfBand = null,
  maxConnectTargets = 8,
  discoveryMetadata = null
} = {}) {
  if (!node || typeof node.broadcastMessage !== 'function') {
    throw new Error('Lobby node is not ready to deliver chat');
  }

  const targets = [...new Set((connectToPeers || []).filter(Boolean))]
    .slice(0, Math.max(1, maxConnectTargets));
  let delivered = false;
  let deliveredVia = '';
  let reachedPeerIds = [];
  let outOfBandPublished = false;
  let outOfBandQueued = false;
  let outOfBandReason = '';

  if (typeof publishOutOfBand === 'function') {
    try {
      const outOfBand = await publishOutOfBand(payload);
      outOfBandPublished = Boolean(outOfBand?.published);
      outOfBandQueued = Boolean(outOfBand?.queued || outOfBand?.advert);
      outOfBandReason = outOfBand?.reason || '';
      if (outOfBandPublished) {
        delivered = true;
        deliveredVia = 'peerbasket';
      }
      lobbyDebug('chat-deliver-oob', {
        type,
        text: payload?.text,
        published: outOfBandPublished,
        queued: outOfBandQueued,
        reason: outOfBandReason
      });
    } catch (error) {
      lobbyDebug('chat-deliver-oob-error', { message: error?.message || String(error) });
    }
  }

  if (targets.length && typeof node.joinDiscovery === 'function' && broadcastScope) {
    await node.joinDiscovery(broadcastScope, {
      ...(discoveryMetadata ? { metadata: discoveryMetadata } : {}),
      bootstrapPeerIds: targets
    }).catch(() => undefined);
  }

  const webrtcAttempts = outOfBandPublished
    ? Math.min(attempts, 2)
    : attempts;

  for (let attempt = 0; attempt < webrtcAttempts; attempt += 1) {
    if (targets.length && typeof node.ensureConnectedToPeers === 'function') {
      await node.ensureConnectedToPeers(targets).catch(() => undefined);
    }
    await sleep(200 + attempt * 150);

    const open = [...new Set(openPeerIds(node).filter(Boolean))];
    const deliveryIds = open.length > 0 ? open : [];

    lobbyDebug('chat-deliver-webrtc-attempt', {
      type,
      attempt,
      targets,
      open,
      deliveryIds
    });

    if (deliveryIds.length === 0) {
      // Further ensureConnected retries only burn PeerJS timeouts (10s×peers).
      lobbyDebug('chat-deliver-webrtc-giveup', {
        type,
        attempt,
        targets,
        reason: 'no-open-channels'
      });
      break;
    }

    let directOk = 0;
    if (typeof node.sendDirectMessage === 'function') {
      await Promise.all(deliveryIds.map(async (peerId) => {
        try {
          await node.sendDirectMessage(peerId, type, payload);
          directOk += 1;
        } catch {
          // Fall through to broadcast below.
        }
      }));
    }

    await node.broadcastMessage(type, payload, {
      broadcastScope,
      connectToPeers: targets.length ? targets : deliveryIds,
      fanoutPeerIds: deliveryIds
    });

    // Re-check open peers after send — PeerJS only delivers on open channels.
    const stillOpen = new Set(openPeerIds(node));
    reachedPeerIds = deliveryIds.filter((peerId) => stillOpen.has(peerId));
    if (reachedPeerIds.length > 0 || directOk > 0) {
      delivered = true;
      deliveredVia = 'webrtc';
      lobbyDebug('chat-deliver-webrtc-ok', {
        type,
        text: payload?.text,
        reachedPeerIds,
        directOk
      });
      break;
    }
  }

  if (!delivered && outOfBandPublished) {
    delivered = true;
    deliveredVia = 'peerbasket';
  }

  await sleep(settleMs);
  const result = {
    delivered,
    deliveredVia,
    reachedPeerIds,
    outOfBandPublished,
    outOfBandQueued,
    outOfBandReason,
    openPeerIds: openPeerIds(node)
  };
  lobbyDebug('chat-deliver-result', {
    type,
    text: payload?.text,
    ...result
  });
  return result;
}

export function mergeChatRooms(existing = [], incoming) {
  const rooms = [...defaultChatRooms()];
  const byId = new Map(rooms.map((room) => [room.roomId, room]));
  for (const room of existing) {
    const id = normalizeRoomId(room?.roomId);
    if (!id || isMainChatRoom(id)) {
      continue;
    }
    byId.set(id, {
      roomId: id,
      title: String(room.title || id).slice(0, CHAT_ROOM_TITLE_MAX),
      createdBy: room.createdBy || '',
      createdAt: room.createdAt || 0,
      builtin: false
    });
  }
  if (incoming) {
    const id = normalizeRoomId(incoming.roomId);
    if (id && !isMainChatRoom(id)) {
      byId.set(id, {
        roomId: id,
        title: String(incoming.title || id).slice(0, CHAT_ROOM_TITLE_MAX),
        createdBy: incoming.createdBy || '',
        createdAt: incoming.createdAt || Date.now(),
        builtin: false
      });
    }
  }
  return [...byId.values()].sort((left, right) => {
    if (left.builtin) {
      return -1;
    }
    if (right.builtin) {
      return 1;
    }
    return Number(left.createdAt || 0) - Number(right.createdAt || 0);
  });
}
